import React from "react";
import { useNavigate } from "react-router-dom";
import { clearAllBessOnboardingCache } from "../../store/bessOnboarding";

interface BessOnboardingSuccessModalProps {
  showModal: boolean;
  onClose: () => void;
  siteName: string;
  companyName: string;
  startDate: string;
  endDate: string;
  monthlyFee: string;
  inverterPower: string;
  partner: string;
  controller: string;
  opportunityCost: string;
  profitShare: string;
  isCombined: boolean;
}

export const BessOnboardingSuccessModal: React.FC<BessOnboardingSuccessModalProps> = ({
  showModal,
  onClose,
  siteName,
  companyName,
  startDate,
  endDate,
  monthlyFee,
  inverterPower,
  partner,
  controller,
  opportunityCost,
  profitShare,
  isCombined,
}) => {
  const productLabel = isCombined ? "Yuso Flex" : "BESS";
  const navigate = useNavigate();

  if (!showModal) return null;

  const handleNavigate = (path: string) => {
    clearAllBessOnboardingCache();
    onClose();
    navigate(path);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={() => handleNavigate("/services")}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-2xl transform transition-all max-w-lg w-full">
          {/* Close button */}
          <button
            onClick={() => handleNavigate("/services")}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Content */}
          <div className="p-8">
            {/* Success Animation Icon */}
            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 mb-6 shadow-lg">
              <svg className="h-12 w-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            {/* Title & Description */}
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{productLabel} Contract Signed!</h3>
              <p className="text-primary-500 text-base leading-relaxed">
                Your {productLabel} contract for <span className="font-semibold">{siteName}</span> has been signed
                successfully. Yuso will now initiate the technical onboarding of your asset.
              </p>
            </div>

            {/* Contract Summary Card */}
            <div className="bg-gradient-to-br rounded-xl p-5 mb-8 border border-gray-200">
              <h4 className="text-xs font-semibold text-primary-600 uppercase tracking-wider mb-4">Contract Summary</h4>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                    <span className="text-sm text-primary-600">Site name</span>
                  </div>
                  <span className="text-sm font-semibold text-primary-600 truncate max-w-[200px]">{siteName}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                    <span className="text-sm text-primary-600">Company</span>
                  </div>
                  <span className="text-sm font-semibold text-primary-600 truncate max-w-[200px]">{companyName}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                    <span className="text-sm text-primary-600">Start date</span>
                  </div>
                  <span className="text-sm font-semibold text-primary-600">{startDate}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                    <span className="text-sm text-primary-600">End date</span>
                  </div>
                  <span className="text-sm font-semibold text-primary-600">{endDate}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                    <span className="text-sm text-primary-600">Inverter power</span>
                  </div>
                  <span className="text-sm font-semibold text-primary-600">{inverterPower}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                    <span className="text-sm text-primary-600">Monthly fee</span>
                  </div>
                  <span className="text-sm font-semibold text-primary-600">{monthlyFee}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                    <span className="text-sm text-primary-600">Profit share</span>
                  </div>
                  <span className="text-sm font-semibold text-primary-600">{profitShare}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                    <span className="text-sm text-primary-600">Opportunity cost</span>
                  </div>
                  <span className="text-sm font-semibold text-primary-600">{opportunityCost}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                    <span className="text-sm text-primary-600">Controller</span>
                  </div>
                  <span className="text-sm font-semibold text-primary-600 truncate max-w-[200px]">{controller}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                    <span className="text-sm text-primary-600">Partner</span>
                  </div>
                  <span className="text-sm font-semibold text-primary-600 truncate max-w-[200px]">{partner}</span>
                </div>
              </div>
            </div>

            {/* Onboarding Next Steps */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <svg
                  className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-green-800">What happens next?</p>
                  <p className="text-xs text-green-600 mt-1">
                    Yuso will begin the technical onboarding of your asset. Once completed, steering of the installation
                    will start automatically.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => handleNavigate("/services")}
                className="w-full bg-primary-600 text-white font-semibold py-3 px-6 rounded-xl hover:bg-primary-700 transform hover:scale-[1.02] transition-all shadow-lg hover:shadow-xl"
              >
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                  Go to Services
                </div>
              </button>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleNavigate("/documents")}
                  className="bg-white border-2 border-primary-600 text-primary-600 font-semibold py-2.5 px-4 rounded-xl hover:bg-primary-50 transform hover:scale-[1.02] transition-all"
                >
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Contracts
                  </div>
                </button>

                <button
                  onClick={() => handleNavigate("/overview")}
                  className="bg-white border-2 border-gray-300 text-gray-700 font-semibold py-2.5 px-4 rounded-xl hover:bg-gray-50 hover:border-gray-400 transform hover:scale-[1.02] transition-all"
                >
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                      />
                    </svg>
                    Overview
                  </div>
                </button>
              </div>
            </div>

            {/* Quick tip */}
            <div className="mt-6 p-3 border border-primary-200 rounded-lg">
              <div className="flex gap-2">
                <p className="text-xs text-primary-700">
                  You'll receive an email confirmation with the contract details shortly. You can track the onboarding
                  progress in the Portfolio section.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
